import React, { useCallback } from 'react';
import jsPDF from 'jspdf';
import type { jsPDF as JSPDFType } from 'jspdf';
import html2canvas from 'html2canvas';
// ReactDOM import removed - using dynamic import for createRoot
import { ItineraryData } from '@/lib/builder/models/itinerary.model';
import { Tables } from '@/integrations/supabase/types';
import { TravelRequest } from '@/lib/request-trip/models/travel-request.model';
import { getActivityTypeLabel } from '@/lib/builder/models/activity-configs.model';
import {
  Bed,
  Calendar,
  Plane,
  FileText,
  FerrisWheel,
  CarTaxiFront,
  MicVocal,
  ClockArrowDown,
  CupSoda
} from "lucide-react";
import { User } from '@supabase/supabase-js';
import { formatTime } from '@/utils/formatTime';

// PDF Configuration Constants
const PDF_CONFIG = {
  // Fonts (using jsPDF built-in fonts)
  FONT_FAMILY: 'helvetica',
  FONT_WEIGHT: {
    NORMAL: 'normal',
    BOLD: 'bold'
  },

  // Colors (RGB values for jsPDF)
  COLORS: {
    BLACK: [0, 0, 0],
    WHITE: [255, 255, 255],
    GRAY_LIGHT: [248, 249, 250], // Very light gray for backgrounds
    GRAY_MEDIUM: [108, 117, 125], // Medium gray for secondary text
    GRAY_DARK: [52, 58, 64], // Dark gray for borders
    GRAY_BORDER: [233, 236, 239], // Light border color
    BRAND_PRIMARY: [3, 10, 20], // Place2Be brand color #030A14
    SUCCESS: [25, 135, 84], // Green for positive elements
    WARNING: [255, 193, 7], // Amber for highlights
    GRAY_INFO: [242, 242, 242], // Light gray for informational text
    GRAY_VALUE: [106, 106, 106], // Gray for labels
  },

  // Font Sizes
  FONT_SIZES: {
    TITLE: 24,
    SECTION_HEADER: 16,
    SUB_SECTION_HEADER: 14,
    ACTIVITY_TITLE: 12,
    BODY_TEXT: 10,
    SMALL_TEXT: 9,
    FOOTER_TEXT: 8,
    LOGO_TEXT: 16,
  },

  // Spacing and Layout
  SPACING: {
    MARGIN: 16,
    LINE_HEIGHT: 6,
    NO_LINE_HEIGHT: 4,
    ACTIVITY_EXTRA_MARGIN: 16,
    SECTION_SPACING: 4,
    HEADER_SPACE: 25,
    FOOTER_SPACE: 25,
    ACTIVITY_INDENT: 5,
    TYPE_INDENT: 10,
  },

  // Logo Configuration
  LOGO: {
    WIDTH: 40,
    HEIGHT: 8,
    CANVAS_WIDTH: 480, // 2x original for high quality
    CANVAS_HEIGHT: 98, // 2x original for high quality
    URL: '/assets/images/dark_extended.svg',
  },

  LOGO_SMALL: {
    WIDTH: 8,
    HEIGHT: 8,
    CANVAS_WIDTH: 20, // 2x original for high quality
    CANVAS_HEIGHT: 20, // 2x original for high quality
    URL: '/assets/images/dark_small.svg',
  },

  IMAGES: {
    MAX_WIDTH: 210, // Max image width in mm
    MAX_HEIGHT: 40, // Max image height in mm
  },

  HEADER: {
    HEIGHT: 12,
  },

  FOOTER: {
    HEIGHT: 20,
  },

  // Icon Configuration
  ICONS: {
    SIZE: 4, // Icon size in mm (reduced from 12)
    CANVAS_SIZE: 16, // Canvas size for high quality (reduced from 24)
    COLOR: [0, 0, 0], // Black color for icons
  },

  // Page Configuration
  PAGE: {
    WIDTH: 210, // A4 width in mm
    HEIGHT: 297, // A4 height in mm
  }
} as const;

// Icon cache to avoid regenerating the same icons
const iconCache = new Map<string, string>();

// Cache for computed content heights to avoid recalculating the same activity multiple times
const contentHeightCache = new Map<string, number>();

const calculateContentHeightCached = (pdf: jsPDF, activity: Tables<'itinerary_activities'>, contentWidth: number, lineHeight: number) => {
  const key = `${activity.id || activity.title}_${contentWidth}_${lineHeight}`;
  if (contentHeightCache.has(key)) return contentHeightCache.get(key)!;
  const value = calculateContentHeight(pdf, activity, contentWidth, lineHeight);
  contentHeightCache.set(key, value);
  return value;
};


// Utility function to clean text for jsPDF compatibility
// Preserve accented and Unicode characters. Only normalize and replace
// known problematic symbols that can affect layout or encoding.
const cleanTextForPDF = (text: string): string => {
  if (!text) return '';
  // Normalizza unicode e sostituisce smart quotes/dash/ellipsis con equivalenti semplici,
  // normalizza spazi e trim finale. Non rimuove accenti.

  return text
    .normalize('NFC')
    // Convert right-facing arrow variants to ASCII arrow
    .replace(/(?:→|➡|➔|➜|➝|⇒|⟶|➞)/g, '->')
    // Convert left-facing arrow variants to ASCII arrow
    .replace(/(?:←|⬅|⇦|⟵)/g, '<-')
    // Normalize smart quotes/apostrophes to simple quotes
    .replace(/[\u2018\u2019\u201A\u201B\u2032]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F\u2033]/g, '"')
    // Replace specific problematic 'a' variants (precomposed and composed) with plain 'a'
    .replace(/\u00E0|a\u0300/g, 'a')
    .replace(/\u00C0|A\u0300/g, 'A')
    // Replace macron a (ā U+0101) and composed a + U+0304 with plain a
    .replace(/\u0101|a\u0304/g, 'a')
    .replace(/\u0100|A\u0304/g, 'A')
    .replace(/\s+/g, ' ') // Normalize whitespace
    .replace(/[\u2018\u2019\u201A\u201B\u2032]/g, "'")   // smart apostrophes
    .replace(/[\u201C\u201D\u201E\u201F\u2033]/g, '"')  // smart quotes
    .replace(/\u2026/g, '...')                         // ellipsis
    .replace(/[\u2013\u2014]/g, '-')                   // ndash, mdash -> -
    .replace(/[\u00A0\u2000-\u200B]/g, ' ')            // no-break & narrow spaces -> normal space
    .replace(/\s+/g, ' ')
    .trim();
};

// Icon mapping to Lucide React components
const getIconComponent = (type: string) => {
  const iconMap: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
    'accommodation': Bed,
    'accommodationOut': ClockArrowDown,
    'activity': FerrisWheel,
    'flight': Plane,
    'transportation': CarTaxiFront,
    'food-drinks': CupSoda,
    'events': MicVocal,
    'notes': FileText
  };

  return iconMap[type] || Calendar; // Calendar as fallback
};

interface ItineraryPDFData {
  itinerary: Tables<'itineraries'>;
  activities: Tables<'itinerary_activities'>[];
  travelRequest?: Tables<'travel_requests'> | TravelRequest; // Support both Supabase type and custom model
  travelDesigner?: Tables<'users'> | User; // Support both Supabase type and custom model
}

export const useItineraryPDF = () => {
  const generatePDF = useCallback(async (itineraryId: string, instantDownload: boolean): Promise<{ pdf: JSPDFType, fileName: string }> => {
    try {
      // Fetch itinerary data
      const { supabase } = await import('@/integrations/supabase/client');

      // Fetch itinerary
      const { data: itinerary, error: itineraryError } = await supabase
        .from('itineraries')
        .select('*')
        .eq('id', itineraryId)
        .single();

      if (itineraryError || !itinerary) {
        throw new Error('Itinerary not found');
      }

      // Fetch activities
      const { data: activities, error: activitiesError } = await supabase
        .from('itinerary_activities')
        .select('*')
        .eq('itinerary_id', itineraryId)
        .order('date', { ascending: true })
        .order('location', { ascending: true });

      if (activitiesError) {
        throw new Error('Failed to fetch activities');
      }

      // Fetch travel request
      const { data: travelRequest } = await supabase
        .from('travel_requests')
        .select('*')
        .eq('id', itinerary.travel_request_id)
        .single();

      const { data: travelDesigner } = await supabase
        .from('users')
        .select('*')
        .eq('id', travelRequest?.assigned_designer_id)
        .single();

      const pdfData: ItineraryPDFData = {
        itinerary,
        activities: activities || [],
        travelRequest,
        travelDesigner
      };

      // Generate PDF
      const pdfEntity = await createItineraryPDF(pdfData);
      if (instantDownload) {
        pdfEntity.pdf.save(pdfEntity.fileName);
      } else {
        return pdfEntity;
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }, []);

  const generatePDFFromData = useCallback(async (itineraryData: ItineraryData) => {
    try {
      // Create a mock itinerary object with required fields
      const mockItinerary = {
        ...itineraryData.getPayload(),
        id: itineraryData.id || 'temp-id',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Convert DayActivity[] to Tables<'itinerary_activities'>[] format
      const mockActivities = itineraryData.itinerary_days?.flatMap(day =>
        day.activities.map(activity => {
          // Helper function to safely get property values
          const getProperty = (obj: unknown, key: string, defaultValue: unknown = '') => {
            return obj && typeof obj === 'object' && key in obj ? (obj as Record<string, unknown>)[key] : defaultValue;
          };

          return {
            id: activity.id,
            itinerary_id: itineraryData.id || 'temp-id',
            title: activity.title,
            description: activity.description,
            location: activity.location,
            type: activity.type,
            date: activity.date,
            include_price: activity.include_price,
            link: getProperty(activity, 'link') as string || '',
            address: getProperty(activity, 'address') as string || '',
            start_date: getProperty(activity, 'start_date') as string || '',
            end_date: getProperty(activity, 'end_date') as string || '',
            start_time: getProperty(activity, 'start_time') as string || '',
            end_time: getProperty(activity, 'end_time') as string || '',
            cost: getProperty(activity, 'cost', 0) as number,
            check_in_time: getProperty(activity, 'check_in_time') as string || '',
            check_out_time: getProperty(activity, 'check_out_time') as string || '',
            check_in_note: getProperty(activity, 'check_in_note') as string || '',
            refundable_rate: getProperty(activity, 'refundable_rate', false) as boolean,
            price_tag: getProperty(activity, 'price_tag') as string || '',
            transport_type: getProperty(activity, 'transport_type') as string || '',
            company: getProperty(activity, 'company') as string || '',
            media: getProperty(activity, 'media', []) as unknown[],
            documentation: getProperty(activity, 'documentation', []) as unknown[],
            travels: getProperty(activity, 'travels', []) as unknown[],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
        })
      ) || [];

      const pdfData: ItineraryPDFData = {
        itinerary: mockItinerary,
        activities: mockActivities,
        travelRequest: itineraryData.travel_request
      };

      const pdfEntity = await createItineraryPDF(pdfData);
      pdfEntity.pdf.save(pdfEntity.fileName);
    } catch (error) {
      console.error('Error generating PDF from data:', error);
      throw error;
    }
  }, []);

  return {
    generatePDF,
    generatePDFFromData
  };
};

const createItineraryPDF = async (data: ItineraryPDFData): Promise<{ pdf: JSPDFType, fileName: string }> => {
  const { itinerary, activities, travelRequest, travelDesigner } = data;

  // Pre-load all required icons to improve performance
  const uniqueActivityTypes = [...new Set(activities.map(activity => activity.type))];
  await Promise.all(uniqueActivityTypes.map(type => createIconBase64(type)));

  // Create new PDF document
  const pdf = new jsPDF('p', 'mm', 'a4');

  pdf.setFont('helvetica', 'normal'); // Use helvetica as fallback
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - (PDF_CONFIG.SPACING.MARGIN * 2);

  // Pure helper function to check page break and return new position
  const checkPageBreak = async (pdf: jsPDF, currentY: number, requiredSpace: number, pageHeight: number, margin: number, pageWidth: number): Promise<number> => {
    const footerSpace = PDF_CONFIG.SPACING.FOOTER_SPACE;
    if ((currentY + requiredSpace + PDF_CONFIG.SPACING.ACTIVITY_EXTRA_MARGIN) > (pageHeight - PDF_CONFIG.SPACING.MARGIN - footerSpace)) {
      addFooter(pdf, pageWidth, pageHeight, 10);
      pdf.addPage();
      // new pages: secondary header (isFirstPage = false)
      const headerHeight = await addHeader(pdf, pageWidth, itinerary, false);
      return PDF_CONFIG.SPACING.MARGIN + headerHeight;
    }
    return currentY;
  };

  // Add header to first page (now returns used height) - first page keeps isFirstPage = true
  const initialHeaderHeight = await addHeader(pdf, pageWidth, itinerary, true);
  let currentY = PDF_CONFIG.SPACING.MARGIN + initialHeaderHeight;

  // FIRST PAGE
  currentY = await createTitleSection(pdf, itinerary, currentY, pageHeight, pageWidth, checkPageBreak);
  currentY = await createTripInformationSection(pdf, itinerary, currentY, contentWidth, pageHeight, pageWidth, checkPageBreak);
  currentY = await createDescriptionSection(pdf, itinerary, currentY, contentWidth, pageHeight, pageWidth, checkPageBreak);
  await addFirstPageFooter(pdf, pageWidth, pageHeight, travelDesigner);

  // DAYS PAGES
  pdf.addPage();
  currentY = PDF_CONFIG.SPACING.MARGIN + initialHeaderHeight;
  const headerHeightAfterFirst = await addHeader(pdf, pageWidth, itinerary, false);
  currentY = PDF_CONFIG.SPACING.MARGIN + headerHeightAfterFirst;

  currentY = await createDailyItinerarySection(pdf, activities, currentY, contentWidth, pageHeight, pageWidth, checkPageBreak);
  currentY = await createTotalSection(pdf, itinerary, activities, PDF_CONFIG.SPACING.MARGIN, currentY, PDF_CONFIG.SPACING.LINE_HEIGHT, contentWidth, pageWidth, pageHeight, checkPageBreak);
  // Footer

  // Save PDF
  const fileName = `itinerary-${itinerary.title.replace(/[^a-zA-Z0-9]/g, '-')}.pdf`;
  return { pdf, fileName };
};

const addFirstPageFooter = async (pdf: jsPDF, pageWidth: number, pageHeight: number, travelDesigner?: Tables<'users'> | User | undefined): Promise<void> => {
  try {
    const footerY = pageHeight - PDF_CONFIG.SPACING.MARGIN - PDF_CONFIG.FOOTER.HEIGHT;

    // Separator line
    pdf.setDrawColor(...PDF_CONFIG.COLORS.GRAY_BORDER);
    pdf.setLineWidth(0.4);
    pdf.line(PDF_CONFIG.SPACING.MARGIN, footerY - 4, pageWidth - PDF_CONFIG.SPACING.MARGIN, footerY - 4);

    // Cell positions
    const contentWidth = pageWidth - PDF_CONFIG.SPACING.MARGIN * 2;
    const halfWidth = contentWidth / 2;
    const leftX = PDF_CONFIG.SPACING.MARGIN + 2;
    const rightX = PDF_CONFIG.SPACING.MARGIN + halfWidth - 2;
    const rightAlignX = pageWidth - PDF_CONFIG.SPACING.MARGIN - 2;
    const startY = footerY + 8; // baseline for first line

    // Extract designer info
    const designer = travelDesigner ?? ({} as any);
    const fullName = (designer.full_name || `${designer.first_name || ''} ${designer.last_name || ''}` || designer.name || '').toString().trim();
    const phone = (designer.phone || '').toString().trim();
    const email = (designer.email || '').toString().trim();

    // Left cell: Name (bold black) then "Travel designer" (gray)
    pdf.setFontSize(12);
    if (fullName) {
      pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.BOLD);
      pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);
      pdf.text(cleanTextForPDF(fullName), leftX, startY);
    } else {
      // if no name, still reserve space (optional)
      pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.BOLD);
      pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);
      pdf.text('', leftX, startY);
    }

    pdf.setFontSize(12);
    pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
    pdf.setTextColor(0x6A, 0x6A, 0x6A); // #6A6A6A
    pdf.text('Travel designer', leftX, startY + PDF_CONFIG.SPACING.LINE_HEIGHT);

    // Right cell: phone (bold) then email then website
    let rightLineY = startY;
    if (phone) {
      pdf.setFontSize(12);
      pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.BOLD);
      pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);
      // right-aligned
      pdf.text(cleanTextForPDF(phone), rightAlignX, rightLineY, { align: 'right' });
      rightLineY += PDF_CONFIG.SPACING.LINE_HEIGHT;
    }

    if (email) {
      pdf.setFontSize(12);
      pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
      pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);
      pdf.text(cleanTextForPDF(email), rightAlignX, rightLineY, { align: 'right' });
      rightLineY += PDF_CONFIG.SPACING.LINE_HEIGHT;
    }

    // Website (always present)
    pdf.setFontSize(12);
    pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
    pdf.setTextColor(...PDF_CONFIG.COLORS.GRAY_MEDIUM);
    pdf.text('www.place2be.it', rightAlignX, rightLineY, { align: 'right' });

    // Optional avatar on the far right (if available) - keep previous behaviour
    const avatarUrl = travelDesigner ? ((travelDesigner as any).avatar_url || (travelDesigner as any).avatar || '') : '';
    if (avatarUrl) {
      try {
        const imgData = await loadImageUrlAsBase64(avatarUrl);
        if (imgData && imgData.base64) {
          const avatarSize = 14;
          const avatarX = pageWidth - PDF_CONFIG.SPACING.MARGIN - avatarSize;
          const avatarY = footerY + 4;
          pdf.addImage(imgData.base64, 'PNG', avatarX, avatarY, avatarSize, avatarSize);
        }
      } catch (err) {
        // ignore avatar errors
      }
    }

  } catch (err) {
    // don't block PDF generation for footer errors
    console.warn('Could not draw first page footer', err);
  }
};
// Section creation functions
const createTitleSection = async (pdf: jsPDF, itinerary: Tables<'itineraries'>, currentY: number, pageHeight: number, pageWidth: number, checkPageBreak: (pdf: jsPDF, currentY: number, requiredSpace: number, pageHeight: number, margin: number, pageWidth: number) => Promise<number>): Promise<number> => {
  // Title with professional styling
  pdf.setFontSize(PDF_CONFIG.FONT_SIZES.TITLE);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.BOLD);
  pdf.setTextColor(...PDF_CONFIG.COLORS.BRAND_PRIMARY);
  pdf.text(cleanTextForPDF(itinerary.title), PDF_CONFIG.SPACING.MARGIN, currentY);
  return currentY + PDF_CONFIG.SPACING.LINE_HEIGHT * 1.5;
};

const createTripInformationSection = async (pdf: jsPDF, itinerary: Tables<'itineraries'>, currentY: number, contentWidth: number, pageHeight: number, pageWidth: number, checkPageBreak: (pdf: jsPDF, currentY: number, requiredSpace: number, pageHeight: number, margin: number, pageWidth: number) => Promise<number>): Promise<number> => {
  // Layout and styles for the four columns
  const keyFontSize = 12;
  const valueFontSize = 12;
  const valueColor = [0, 0, 0];

  // Prepare values
  const startDate = itinerary.start_date ? new Date(itinerary.start_date).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric'
  }) : '';
  const endDate = itinerary.end_date ? new Date(itinerary.end_date).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric'
  }) : '';

  const guestsData = Array.isArray(itinerary.guests_data) ? itinerary.guests_data : [];
  const guestsText = `${guestsData.length}`;

  const numberOfGuests = Array.isArray(itinerary.guests_data) ? itinerary.guests_data.length : 1;
  const totalForPerson = (itinerary.total / numberOfGuests).toFixed(2);
  const budgetText = `€ ${totalForPerson.toLocaleString()}`;

  const columns = [
    { key: 'From', value: cleanTextForPDF(startDate) },
    { key: 'To', value: cleanTextForPDF(endDate) },
    { key: 'Guests', value: cleanTextForPDF(guestsText) },
    { key: 'Price per person', value: budgetText } // EUR symbol kept
  ];

  const colCount = columns.length;
  const COLWIDTH = contentWidth / colCount;
  const rowX = PDF_CONFIG.SPACING.MARGIN;
  const rowY = currentY;

  // For each column, render key (grigio) e value (bold, nero) su riga successiva con wrapping per il valore
  const valueLinesPerCol = columns.map(col => {
    // Use available width inside column
    const lines = pdf.splitTextToSize(col.value || '', COLWIDTH + 10); // padding per non andare a capo con le date
    return lines;
  });

  // Compute max lines (1 line for key + 1 lines for value)
  const maxLines = 2;

  for (let i = 0; i < colCount; i++) {
    const col = columns[i];
    const x = rowX + (i * COLWIDTH);

    // Key (small/regular, gray)
    pdf.setFontSize(keyFontSize);
    pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
    pdf.setTextColor(...PDF_CONFIG.COLORS.GRAY_VALUE);
    pdf.text(cleanTextForPDF(col.key), x, rowY);

    // Value (bold, black) - allow wrapping
    pdf.setFontSize(valueFontSize);
    pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.BOLD);
    pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);
    const vLines = valueLinesPerCol[i].length ? valueLinesPerCol[i] : [''];
    // Value start Y is one line below key
    const valueStartY = rowY + PDF_CONFIG.SPACING.LINE_HEIGHT;
    pdf.text(vLines, x, valueStartY);
  }

  // Advance Y by the tallest column content (key + value lines)
  currentY = rowY + (maxLines * PDF_CONFIG.SPACING.LINE_HEIGHT);

  // Small spacing after the row
  return currentY + 8;
};

const createDescriptionSection = async (pdf: jsPDF, itinerary: Tables<'itineraries'>, currentY: number, contentWidth: number, pageHeight: number, pageWidth: number, checkPageBreak: (pdf: jsPDF, currentY: number, requiredSpace: number, pageHeight: number, margin: number, pageWidth: number) => Promise<number>): Promise<number> => {
  if (!itinerary.description) return currentY;

  const cleanDescription = cleanTextForPDF(itinerary.description);

  // Calculate available space on first page (accounting for footer and margins)
  const footerSpace = PDF_CONFIG.SPACING.FOOTER_SPACE + PDF_CONFIG.FOOTER.HEIGHT;
  const availableSpace = pageHeight - currentY - PDF_CONFIG.SPACING.MARGIN - footerSpace;

  // Try different font sizes from largest to smallest
  const fontSizes = [
    PDF_CONFIG.FONT_SIZES.SUB_SECTION_HEADER, // 14
    PDF_CONFIG.FONT_SIZES.ACTIVITY_TITLE,     // 12
    PDF_CONFIG.FONT_SIZES.BODY_TEXT,          // 10
    PDF_CONFIG.FONT_SIZES.SMALL_TEXT          // 9
  ];

  let selectedFontSize = fontSizes[fontSizes.length - 1]; // Default to smallest
  let descriptionLines: string[] = [];

  // Find the largest font size that fits in available space
  for (const fontSize of fontSizes) {
    pdf.setFontSize(fontSize);
    pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
    const lines = pdf.splitTextToSize(cleanDescription, contentWidth);
    const requiredHeight = lines.length * PDF_CONFIG.SPACING.LINE_HEIGHT;

    if (requiredHeight <= availableSpace) {
      selectedFontSize = fontSize;
      descriptionLines = lines;
      break;
    }
  }

  // If still too large with smallest font, use smallest font anyway
  if (descriptionLines.length === 0) {
    pdf.setFontSize(fontSizes[fontSizes.length - 1]);
    pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
    descriptionLines = pdf.splitTextToSize(cleanDescription, contentWidth);
  }

  // Apply the selected font size and render
  pdf.setFontSize(selectedFontSize);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
  pdf.text(descriptionLines, PDF_CONFIG.SPACING.MARGIN, currentY);

  return currentY + (descriptionLines.length * PDF_CONFIG.SPACING.LINE_HEIGHT);
};

const createDailyItinerarySection = async (pdf: jsPDF, activities: Tables<'itinerary_activities'>[], currentY: number, contentWidth: number, pageHeight: number, pageWidth: number, checkPageBreak: (pdf: jsPDF, currentY: number, requiredSpace: number, pageHeight: number, margin: number, pageWidth: number) => Promise<number>): Promise<number> => {
  // Group activities by date
  const activitiesByDate = activities.reduce((acc, activity) => {
    const date = activity.date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(activity);
    return acc;
  }, {} as Record<string, Tables<'itinerary_activities'>[]>);

  // Sort dates
  const sortedDates = Object.keys(activitiesByDate).sort();

  // Daily Itinerary header
  pdf.setFontSize(PDF_CONFIG.FONT_SIZES.SECTION_HEADER);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.BOLD);
  pdf.text('Daily Itinerary', PDF_CONFIG.SPACING.MARGIN, currentY);
  currentY += PDF_CONFIG.SPACING.LINE_HEIGHT * 2;

  for (const date of sortedDates) {
    const dayActivities = activitiesByDate[date];
    const dayDate = new Date(date);
    const dayName = dayDate.toLocaleDateString('en-US', { weekday: 'long' });
    const dayNumber = dayDate.getDate();
    const monthName = dayDate.toLocaleDateString('en-US', { month: 'long' });

    // Ensure activities for the same date are ordered by location as a tie-breaker
    dayActivities.sort((a, b) => {
      const la = (a.location || '')?.toString();
      const lb = (b.location || '')?.toString();
      return la.localeCompare(lb, undefined, { numeric: true, sensitivity: 'base' });
    });

    // Day header
    const estimatedFirstActivityHeight = calculateContentHeightCached(pdf, dayActivities[0], contentWidth, PDF_CONFIG.SPACING.LINE_HEIGHT);
    currentY = await checkPageBreak(pdf, currentY, estimatedFirstActivityHeight + PDF_CONFIG.HEADER.HEIGHT, pageHeight, PDF_CONFIG.SPACING.MARGIN, pageWidth);
    currentY = await createDayHeader(pdf, dayName, dayNumber, monthName, PDF_CONFIG.SPACING.MARGIN, currentY, contentWidth);

    // Activities for this day
    for (const activity of dayActivities) {
      const estimatedActivityHeight = calculateContentHeightCached(pdf, activity, contentWidth, PDF_CONFIG.SPACING.LINE_HEIGHT);
      currentY = await checkPageBreak(pdf, currentY, estimatedActivityHeight, pageHeight, PDF_CONFIG.SPACING.MARGIN, pageWidth);
      currentY = await createActivityCard(pdf, activity, PDF_CONFIG.SPACING.MARGIN, currentY, PDF_CONFIG.SPACING.LINE_HEIGHT, contentWidth);
    }

    currentY += PDF_CONFIG.SPACING.SECTION_SPACING;
  }

  return currentY;
};

const createTotalSection = async (
  pdf: jsPDF,
  itinerary: Tables<'itineraries'>,
  activities: Tables<'itinerary_activities'>[],
  margin: number,
  currentY: number,
  lineHeight: number,
  contentWidth: number,
  pageWidth: number,
  pageHeight: number,
  checkPageBreak: (pdf: jsPDF, currentY: number, requiredSpace: number, pageHeight: number, margin: number, pageWidth: number) => Promise<number>
): Promise<number> => {
  // Reserve space for three text lines (key, big total, helper message)
  const approxHeight = (8) + (10) + (10) + 12; // safe mm estimate
  currentY = await checkPageBreak(pdf, currentY, approxHeight, pageHeight, margin, pageWidth);

  const totalCost = itinerary.total || 0;
  const leftX = margin;

  // "Total:" label - gray
  pdf.setFontSize(12);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
  pdf.setTextColor(...PDF_CONFIG.COLORS.GRAY_VALUE);
  pdf.text('Total: ', leftX, currentY);

  // Total amount on next line - black, 16px (use 16 as fontSize)
  const totalY = currentY + 8; // spacing between lines
  pdf.setFontSize(16);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.BOLD);
  pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);
  pdf.text(`€ ${Number(totalCost).toLocaleString()}`, leftX, totalY);

  // Helper message on next line - gray, 10px
  const msgY = totalY + 10;
  pdf.setFontSize(10);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
  pdf.setTextColor(...PDF_CONFIG.COLORS.GRAY_VALUE);
  pdf.text('Check your email to complete the payment using the method you selected.', leftX, msgY);

  // Return new Y position with small bottom spacing
  return msgY + 8;
};

const createDayHeader = async (pdf: jsPDF, dayName: string, dayNumber: number, monthName: string, margin: number, currentY: number, contentWidth: number): Promise<number> => {
  pdf.setFontSize(PDF_CONFIG.FONT_SIZES.SUB_SECTION_HEADER);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.BOLD);

  const headerY = currentY - 5;

  // Main background
  pdf.setFillColor(...PDF_CONFIG.COLORS.GRAY_INFO);
  pdf.rect(margin, headerY, contentWidth, PDF_CONFIG.HEADER.HEIGHT, 'F');

  // Center text vertically in the header block
  const textY = headerY + (PDF_CONFIG.HEADER.HEIGHT / 2) + 2;
  pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);
  pdf.text(`${monthName} ${dayNumber} - ${dayName}`, margin + 8, textY);

  // Reset text color
  pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);
  return headerY + PDF_CONFIG.HEADER.HEIGHT + 8; // End of header + 8px spacing
};

const calculateContentHeight = (pdf: jsPDF, activity: Tables<'itinerary_activities'>, contentWidth: number, lineHeight: number): number => {
  let totalHeight = 0;
  const lineSpacing = 1; // Extra spacing between lines

  // Helper function to calculate label width
  const getLabelWidth = (label: string): number => {
    const currentSize = pdf.getFontSize();
    pdf.setFontSize(PDF_CONFIG.FONT_SIZES.BODY_TEXT);
    const width = pdf.getTextWidth(label);
    pdf.setFontSize(currentSize);
    return width;
  };

  // Type badge height (first row)
  totalHeight += PDF_CONFIG.SPACING.LINE_HEIGHT + lineSpacing;

  // Title height (second row)
  totalHeight += PDF_CONFIG.SPACING.LINE_HEIGHT;

  // Location height
  if (activity.address) {
    totalHeight += PDF_CONFIG.SPACING.LINE_HEIGHT + lineSpacing;
  }

  // Type-specific details height
  switch (activity.type) {
    case 'accommodation':
      if (activity.start_date && activity.end_date) totalHeight += PDF_CONFIG.SPACING.NO_LINE_HEIGHT * 2;
      if (activity.check_in_note) totalHeight += PDF_CONFIG.SPACING.NO_LINE_HEIGHT + lineSpacing;
      break;
    case 'activity':
    case 'events':
      if (activity.start_date) totalHeight += lineHeight + lineSpacing;
      if (activity.end_date) totalHeight += lineHeight + lineSpacing;
      break;
    case 'flight':
      if (activity.travels && Array.isArray(activity.travels)) {
        const travelsArray = activity.travels as unknown[];
        travelsArray.forEach((travel, index) => {
          const travelData = travel as { flightNumber?: string; airline?: string; from?: string; to?: string; fromDate?: string; fromTime?: string; toDate?: string; toTime?: string };
          let flightFields = 0;
          if (travelData.flightNumber) {
            const flightNumberText = cleanTextForPDF(travelData.flightNumber);
            flightFields += flightNumberText.includes(' -> ') ? 2 : 1;
          }
          if (travelData.airline) flightFields++;
          if (travelData.from && travelData.to) flightFields += 2;
          if (travelData.fromDate && travelData.fromTime) flightFields++;
          if (travelData.toDate && travelData.toTime) flightFields++;
          totalHeight += flightFields * (lineHeight + lineSpacing);
        });
      }
      break;
    case 'transportation':
      if (activity.transport_type) totalHeight += lineHeight + lineSpacing;
      if (activity.company) totalHeight += lineHeight + lineSpacing;
      if (activity.travels && Array.isArray(activity.travels)) {
        const travelsArray = activity.travels as unknown[];
        totalHeight += travelsArray.length * ((lineHeight + lineSpacing) * 2); // Each travel has from/to
      }
      break;
    case 'food-drinks':
    case 'notes':
      if (activity.link) {
        // Calculate wrapped link lines
        const linkText = cleanTextForPDF(activity.link);
        const labelWidth = getLabelWidth('Link: ');
        const availableWidth = Math.max(0, contentWidth - 16 - labelWidth);
        const linkLines = pdf.splitTextToSize(linkText, availableWidth);
        totalHeight += (linkLines.length * PDF_CONFIG.SPACING.NO_LINE_HEIGHT) + lineSpacing;
      }
      break;
  }

  // Description height (with text wrapping) - mirror rendering widths used in createActivityCard
  if (activity.description) {
    // Exclude description for flight and transportation (hidden for client)
    if (!(activity.type === 'flight' || activity.type === 'transportation')) {
      const cleanDesc = cleanTextForPDF(activity.description);
      const contentPadding = 4; // must match renderer
      const descAvailableWidth = Math.max(0, contentWidth - (contentPadding * 2) - 8);

      // Calculate width of "Description: " label using same font settings
      const labelWidth = getLabelWidth('Description: ');

      // First line has less space because of the label
      const firstLineWidth = Math.max(0, descAvailableWidth - labelWidth);

      // Split considering label on first line, then remaining lines use full descAvailableWidth
      const firstLineSplit = pdf.splitTextToSize(cleanDesc, firstLineWidth);
      let totalLines = 0;
      if (firstLineSplit.length === 0) {
        totalLines = 0;
      } else {
        totalLines = 1; // first line
        if (firstLineSplit.length > 1) {
          const remaining = pdf.splitTextToSize(firstLineSplit.slice(1).join(' '), descAvailableWidth);
          totalLines += remaining.length;
        }
      }

      totalHeight += totalLines * PDF_CONFIG.SPACING.NO_LINE_HEIGHT;
    }
  }

  return totalHeight; // content height only; padding added by renderer
};

const createActivityCard = async (pdf: jsPDF, activity: Tables<'itinerary_activities'>, margin: number, currentY: number, lineHeight: number, contentWidth: number): Promise<number> => {
  // Calculate actual content height (without image placeholder) using cached wrapper
  const contentHeight = calculateContentHeightCached(pdf, activity, contentWidth, lineHeight);

  const cardPadding = 16; // 8px top + 8px bottom
  const activityCardY = currentY - 3;
  const cardX = margin;
  const cardWidth = contentWidth;
  const contentPadding = 4; // left/right inner padding in mm (was 4px earlier)

  // If activity has media, preload and compute max image height (mm) BEFORE drawing the card
  let maxImgHeightUsed = 0;
  let loadedImages: Array<{ base64: string | null; width: number; height: number } | null> = [];
  if (activity.media && Array.isArray(activity.media) && activity.media.length > 0) {
    const mediaUrls = activity.media.filter(Boolean).slice(0, 4) as string[];
    if (mediaUrls.length > 0) {
      const gap = 3; // mm gap between image boxes
      const maxRowWidth = cardWidth - (contentPadding * 2); // account small content padding when measuring
      const imgCount = mediaUrls.length;
      const maxQuarterWidth = maxRowWidth / 4; // each image box max 1/4 of row
      const boxWidth = Math.min((maxRowWidth - gap * (imgCount - 1)) / imgCount, maxQuarterWidth);
      const imgInnerPadding = (8 / 96) * 25.4; // 8px -> mm
      const imgAvailableWidth = Math.max(0, boxWidth - imgInnerPadding * 2);
      const pxToMm = (px: number) => (px / 96) * 25.4; // assume 96dpi

      // preload all images (optimized loader) - use lower resolution for media to save size
      loadedImages = await Promise.all(mediaUrls.map(async (url) => {
        try {
          const imgData = await loadActivityImageOptimized(url, 500); // 500px for activity media
          return imgData;
        } catch (err) {
          return null;
        }
      }));

      for (let i = 0; i < loadedImages.length; i++) {
        const imgData = loadedImages[i];
        if (!imgData || !imgData.base64) continue;

        const originalWidthMM = pxToMm(imgData.width || 1);
        const originalHeightMM = pxToMm(imgData.height || 1);
        const scale = originalWidthMM > 0 ? imgAvailableWidth / originalWidthMM : 1;
        let imgHeightMM = Math.max(0, originalHeightMM * scale);

        const maxImgHeightCap = PDF_CONFIG.IMAGES.MAX_HEIGHT; // mm cap per image
        if (imgHeightMM > maxImgHeightCap) {
          imgHeightMM = maxImgHeightCap;
        }

        const usedHeight = imgInnerPadding + imgHeightMM + imgInnerPadding;
        if (usedHeight > maxImgHeightUsed) maxImgHeightUsed = usedHeight;
      }
    }
  }

  // Compute final card height including image row height
  const activityCardHeight = Math.max(contentHeight + cardPadding + maxImgHeightUsed + PDF_CONFIG.SPACING.ACTIVITY_INDENT, 8); // Minimum height of 8mm

  // Draw card background and border
  pdf.setFillColor(...PDF_CONFIG.COLORS.WHITE);
  pdf.rect(cardX + 2, activityCardY + 2, cardWidth - 4, activityCardHeight - 4, 'F');
  pdf.rect(cardX + 1, activityCardY + 1, cardWidth - 2, 1, 'F');
  pdf.rect(cardX, activityCardY, cardWidth, 1, 'F');
  pdf.rect(cardX + 1, activityCardY + activityCardHeight - 2, cardWidth - 2, 1, 'F');
  pdf.rect(cardX, activityCardY + activityCardHeight - 1, cardWidth, 1, 'F');
  pdf.setDrawColor(...PDF_CONFIG.COLORS.GRAY_BORDER);
  pdf.setLineWidth(0.3);
  pdf.rect(cardX, activityCardY, cardWidth, activityCardHeight, 'S');

  // Activity type label and icon
  const typeLabel = getActivityTypeLabel(activity.type as 'accommodation' | 'activity' | 'flight' | 'transportation' | 'food-drinks' | 'events' | 'notes');
  const typeIconBase64 = await createIconBase64(activity.type);

  // Add 4px padding to content positioning (left and right only)
  const topPadding = 8; // Additional top padding for better spacing
  const contentStartY = activityCardY + topPadding;
  let contentY = contentStartY;

  // Add icon image if available
  if (typeIconBase64) {
    try {
      const iconY = contentY - (PDF_CONFIG.ICONS.SIZE / 2) - 1;
      pdf.addImage(typeIconBase64, 'PNG', cardX + contentPadding + 2, iconY, PDF_CONFIG.ICONS.SIZE, PDF_CONFIG.ICONS.SIZE);
    } catch (error) {
      console.warn('Could not add icon image:', error);
    }
  }

  // Add activity type label in a left-aligned badge with rounded corners (first row)
  pdf.setFontSize(PDF_CONFIG.FONT_SIZES.SMALL_TEXT);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);

  // Create badge with dynamic width calculation
  const badgePadding = 3; // Internal padding
  const textWidth = pdf.getTextWidth(typeLabel); // Calculate actual text width
  const badgeWidth = textWidth + (badgePadding * 2); // Width with padding
  const badgeHeight = 6; // Slightly taller for better proportions
  const badgeX = typeIconBase64 ? cardX + contentPadding + 10 : cardX + contentPadding + 2;
  const badgeY = contentY - 4;

  // Badge border only (no background color)
  pdf.setDrawColor(...PDF_CONFIG.COLORS.GRAY_BORDER);
  pdf.setLineWidth(0.2);
  pdf.rect(badgeX, badgeY, badgeWidth, badgeHeight, 'S');

  // Badge text with internal padding
  pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);
  pdf.text(typeLabel, badgeX + badgePadding, contentY);

  // Price / Not included label: render at the right end of the badge row
  try {
    const priceX = cardX + cardWidth - contentPadding - 2; // right edge with small padding
    const priceY = contentY; // align vertically with badge text

    pdf.setFontSize(PDF_CONFIG.FONT_SIZES.SMALL_TEXT);

    if (activity.include_price && activity.type !== 'notes') {
      // included label (subtle gray)
      pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
      pdf.setTextColor(...PDF_CONFIG.COLORS.GRAY_DARK);
      pdf.text('Included', priceX, priceY, { align: 'right' });
    } else {
      // Not included label (subtle gray)
      pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
      pdf.setTextColor(...PDF_CONFIG.COLORS.GRAY_VALUE);
      pdf.text('Excluded', priceX, priceY, { align: 'right' });
    }
  } catch (err) {
    // Don't break PDF rendering for price label errors
    console.warn('Could not render price label for activity:', err);
  }

  contentY += lineHeight * 1.2;

  // Add activity title on second row
  pdf.setFontSize(PDF_CONFIG.FONT_SIZES.ACTIVITY_TITLE);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.BOLD);
  pdf.setTextColor(...PDF_CONFIG.COLORS.BRAND_PRIMARY);
  const titleX = cardX + contentPadding + 2;

  // For flight activities, extract airport codes from title if it contains "->"
  let titleText = cleanTextForPDF(activity.title);
  if (activity.type === 'flight') {
    // Support multiple arrow/dash variants (->, →, –, —, -) and optional spaces
    const parts = titleText.split(/\s*(?:->|→|–|—|-)\s*/);
    if (parts.length >= 2) {
      const from = parts[0];
      const to = parts.slice(1).join(' ');
      const fromMatch = from.match(/\(([^)]+)\)/);
      const toMatch = to.match(/\(([^)]+)\)/);
      const fromCode = fromMatch ? fromMatch[1] : from.split(' - ')[0].trim();
      const toCode = toMatch ? toMatch[1] : to.split(' - ')[0].trim();
      titleText = `From ${fromCode} to ${toCode}`;
    }
  }

  pdf.text(cleanTextForPDF(titleText), titleX, contentY);
  contentY += lineHeight;

  // Activity details based on type
  pdf.setFontSize(PDF_CONFIG.FONT_SIZES.BODY_TEXT);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
  pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);

  // Address (common to all activities)
  if (activity.address) {
    pdf.text(`Address: ${cleanTextForPDF(activity.address)}`, cardX + contentPadding + 2, contentY);
    contentY += lineHeight;
  }

  // Type-specific information
  contentY = await addActivityDetails(pdf, activity, cardX + contentPadding + 2, contentY, lineHeight, contentWidth);

  // Description (common to all activities) - use wrapping
  if (activity.description && !(activity.type === 'flight' || activity.type === 'transportation')) {
    const cleanDesc = cleanTextForPDF(activity.description);
    const descAvailableWidth = Math.max(0, cardWidth - (contentPadding * 2) - 8);

    // Calculate width of "Description: " label
    const labelWidth = pdf.getTextWidth('Description: ');

    // Split text considering the label width on first line
    const firstLineWidth = descAvailableWidth - labelWidth;
    const descLines = pdf.splitTextToSize(cleanDesc, firstLineWidth);

    if (descLines.length > 0) {
      // Write label + first line
      pdf.text(`Description: ${cleanTextForPDF(descLines[0])}`, cardX + contentPadding + 2, contentY);
      contentY += PDF_CONFIG.SPACING.NO_LINE_HEIGHT;

      // Write remaining lines (if any) with full width
      if (descLines.length > 1) {
        const remainingLines = pdf.splitTextToSize(cleanTextForPDF(descLines.slice(1).join(' ')), descAvailableWidth);
        pdf.text(remainingLines, cardX + contentPadding + 2, contentY);
        contentY += remainingLines.length * PDF_CONFIG.SPACING.NO_LINE_HEIGHT;
      }
    } else {
      pdf.text('Description:', cardX + contentPadding + 2, contentY);
      contentY += PDF_CONFIG.SPACING.NO_LINE_HEIGHT;
    }
  }

  if (loadedImages && Array.isArray(loadedImages) && loadedImages.length > 0) {
    const mediaImgs = loadedImages.filter(Boolean) as Array<{ base64: string | null; width: number; height: number }>;
    if (mediaImgs.length > 0) {
      const gap = 3; // mm gap between image boxes
      const maxRowWidth = cardWidth - (contentPadding * 2);
      const imgCount = mediaImgs.length;
      const maxQuarterWidth = maxRowWidth / 4; // each image box max 1/4 of row
      const boxWidth = Math.min((maxRowWidth - gap * (imgCount - 1)) / imgCount, maxQuarterWidth);
      const imgInnerPadding = (8 / 96) * 25.4; // 8px -> mm
      const imgAvailableWidth = Math.max(0, boxWidth - imgInnerPadding * 2);
      const pxToMm = (px: number) => (px / 96) * 25.4; // assume 96dpi

      for (let i = 0; i < mediaImgs.length; i++) {
        const imgData = mediaImgs[i];
        if (!imgData || !imgData.base64) continue;

        try {
          const originalWidthMM = pxToMm(imgData.width || 1);
          const originalHeightMM = pxToMm(imgData.height || 1);
          const scale = originalWidthMM > 0 ? imgAvailableWidth / originalWidthMM : 1;
          let imgHeightMM = Math.max(0, originalHeightMM * scale);
          let imgWidthMM = imgAvailableWidth;

          const maxImgHeightCap = PDF_CONFIG.IMAGES.MAX_HEIGHT; // mm
          if (imgHeightMM > maxImgHeightCap) {
            const capScale = maxImgHeightCap / imgHeightMM;
            imgHeightMM = maxImgHeightCap;
            imgWidthMM = imgWidthMM * capScale;
          }

          const boxX = cardX + contentPadding + i * (boxWidth + gap);
          const boxY = contentY;

          const imgX = boxX + imgInnerPadding + ((boxWidth - imgInnerPadding * 2) - imgWidthMM) / 2;
          const imgY = boxY + imgInnerPadding;

          // detect format from data URL
          const dataUrl = imgData.base64;
          const format = dataUrl && dataUrl.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG';
          pdf.addImage(dataUrl as string, format as any, imgX, imgY, imgWidthMM, imgHeightMM);
        } catch (err) {
          console.warn('Could not render preloaded activity media image:', err);
        }
      }
    }
  }
  // Update currentY to the end of this card
  return activityCardY + activityCardHeight + 8; // spacing after card
};

const addActivityDetails = async (pdf: jsPDF, activity: Tables<'itinerary_activities'>, startX: number, contentY: number, lineHeight: number, contentWidth: number = 170): Promise<number> => {
  switch (activity.type) {
    case 'accommodation':
      // Print check-in/check-out dates including times when available
      if (activity.start_date && activity.end_date) {
        const checkInTime = activity.check_in_time ? ` - ${activity.check_in_time}` : '';
        const checkOutTime = activity.check_out_time ? ` - ${activity.check_out_time}` : '';
        pdf.text(`Check-in: ${activity.start_date}${checkInTime}`, startX, contentY);
        contentY += lineHeight;
        pdf.text(`Check-out: ${activity.end_date}${checkOutTime}`, startX, contentY);
        contentY += lineHeight;
      }
      if (activity.check_in_note) {
        pdf.text(`Notes: ${cleanTextForPDF(activity.check_in_note)}`, startX, contentY);
        contentY += lineHeight;
      }
      break;

    case 'activity':
    case 'events':
      if (activity.start_date && activity.end_date) {
        pdf.text(`Date: ${activity.start_date}`, startX, contentY);
        contentY += lineHeight;
      }
      if (activity.start_time && activity.end_time) {
        pdf.text(`Time: ${formatTime(activity.start_time)} - ${formatTime(activity.end_time)}`, startX, contentY);
        contentY += lineHeight;
      }
      break;

    case 'flight':
      if (activity.travels && Array.isArray(activity.travels) && activity.travels.length > 0) {
        const travelsArray = activity.travels as unknown[];
        travelsArray.forEach((travel, index: number) => {
          const travelData = travel as { flightNumber?: string; airline?: string; from?: string; to?: string; fromDate?: string; fromTime?: string; toDate?: string; toTime?: string };
          // Add flight number prefix for multiple flights
          const flightPrefix = travelsArray.length > 1 ? `Flight ${index + 1}: ` : '';

          if (travelData.flightNumber) {
            pdf.text(`${flightPrefix}${cleanTextForPDF(travelData.flightNumber)}`, startX, contentY);
            contentY += lineHeight;
          }
          if (travelData.airline) {
            pdf.text(`Airline: ${cleanTextForPDF(travelData.airline)}`, startX, contentY);
            contentY += lineHeight;
          }
          if (travelData.from && travelData.to) {
            pdf.text(`From: ${cleanTextForPDF(travelData.from)}`, startX, contentY);
            contentY += lineHeight;
            pdf.text(`To: ${cleanTextForPDF(travelData.to)}`, startX, contentY);
            contentY += lineHeight;
          }
          if (travelData.fromDate && travelData.fromTime) {
            pdf.text(`Departure: ${cleanTextForPDF(travelData.fromDate)} at ${cleanTextForPDF(travelData.fromTime)}`, startX, contentY);
            contentY += lineHeight;
          }
          if (travelData.toDate && travelData.toTime) {
            pdf.text(`Arrival: ${cleanTextForPDF(travelData.toDate)} at ${cleanTextForPDF(travelData.toTime)}`, startX, contentY);
            contentY += lineHeight;
          }

          // Add spacing between multiple flights
          if (index < travelsArray.length - 1) {
            contentY += lineHeight * 0.5; // Half line spacing between flights
          }
        });
      }
      break;

    case 'transportation':
      if (activity.transport_type) {
        pdf.text(`Transport: ${cleanTextForPDF(activity.transport_type.toUpperCase())}`, startX, contentY);
        contentY += lineHeight;
      }
      if (activity.company) {
        pdf.text(`Company: ${cleanTextForPDF(activity.company)}`, startX, contentY);
        contentY += lineHeight;
      }
      if (activity.travels && Array.isArray(activity.travels) && activity.travels.length > 0) {
        const travelsArray = activity.travels as unknown[];
        travelsArray.forEach((travel, index: number) => {
          const travelData = travel as { address?: string; date?: string; time?: string };
          if (travelData.address) {
            pdf.text(`Address: ${cleanTextForPDF(travelData.address)}`, startX, contentY);
            contentY += lineHeight;
          }
          if (travelData.date && travelData.time) {
            pdf.text(`Date: ${cleanTextForPDF(travelData.date)} at ${cleanTextForPDF(travelData.time)}`, startX, contentY);
            contentY += lineHeight;
          }
        });
      }
      break;

    case 'food-drinks':
    case 'notes':
      if (activity.link) {
        const linkText = cleanTextForPDF(activity.link);
        // Calculate available width for the link after "Link: " label
        const labelWidth = pdf.getTextWidth('Link: ');
        const availableWidth = Math.max(0, (contentWidth || 170) - 16 - labelWidth);

        // Split link into multiple lines if needed
        const linkLines = pdf.splitTextToSize(linkText, availableWidth);

        // Render label and first line together
        pdf.text(`Link: ${linkLines[0] || ''}`, startX, contentY);
        contentY += PDF_CONFIG.SPACING.NO_LINE_HEIGHT;

        // Render remaining lines if any (indented slightly)
        if (linkLines.length > 1) {
          for (let i = 1; i < linkLines.length; i++) {
            pdf.text(linkLines[i], startX + 6, contentY);
            contentY += PDF_CONFIG.SPACING.NO_LINE_HEIGHT;
          }
        }
        contentY += PDF_CONFIG.SPACING.LINE_HEIGHT
      }
      break;
  }

  return contentY;
};

const addHeader = async (pdf: jsPDF, pageWidth: number, itinerary?: Tables<'itineraries'>, isFirstPage: boolean = true): Promise<number> => {
  let usedHeight = 0;
  const contentWidth = pageWidth - (PDF_CONFIG.SPACING.MARGIN * 2);

  try {
    // Try to load the Place2Be logo as base64
    const logoBase64 = await loadLogoAsBase64(isFirstPage ? 'large' : 'small');

    if (logoBase64) {
      const logoWidth = isFirstPage ? PDF_CONFIG.LOGO.WIDTH : PDF_CONFIG.LOGO_SMALL.WIDTH;
      const logoHeight = isFirstPage ? PDF_CONFIG.LOGO.HEIGHT : PDF_CONFIG.LOGO_SMALL.HEIGHT;
      const logoY = PDF_CONFIG.SPACING.MARGIN; // top margin

      if (isFirstPage) {
        // Center logo horizontally on first page
        const logoX = (pageWidth - logoWidth) / 2;
        pdf.addImage(logoBase64, 'PNG', logoX, logoY, logoWidth, logoHeight);
      } else {
        // Secondary header on subsequent pages: place logo left-aligned
        const logoX = PDF_CONFIG.SPACING.MARGIN;
        pdf.addImage(logoBase64, 'PNG', logoX, logoY, logoWidth, logoHeight);
      }

      usedHeight = logoY + logoHeight;
    } else {
      throw new Error('Logo not loaded');
    }

  } catch (error) {
    // Fallback to text representation
    pdf.setFontSize(PDF_CONFIG.FONT_SIZES.LOGO_TEXT);
    pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.BOLD);
    pdf.setTextColor(...PDF_CONFIG.COLORS.BRAND_PRIMARY);

    const logoText = 'Place2Be';
    const textWidth = pdf.getTextWidth(logoText);
    const textY = PDF_CONFIG.SPACING.MARGIN + 8;

    if (isFirstPage) {
      const textX = (pageWidth - textWidth) / 2;
      pdf.text(logoText, textX, textY);
    } else {
      // left aligned fallback for secondary header
      pdf.text(logoText, PDF_CONFIG.SPACING.MARGIN, textY);
    }

    usedHeight = textY;
  }

  // Insert itinerary cover image under the logo only on the first page
  if (isFirstPage) {
    // Load and draw the cover image via helper
    usedHeight = await loadCoverImage(pdf, itinerary, pageWidth, contentWidth, usedHeight);
  }

  // Date/text styling (no separator line)
  pdf.setFontSize(PDF_CONFIG.FONT_SIZES.SMALL_TEXT);
  pdf.setFont(PDF_CONFIG.FONT_FAMILY, PDF_CONFIG.FONT_WEIGHT.NORMAL);
  pdf.setTextColor(...PDF_CONFIG.COLORS.BLACK);

  return usedHeight;
};

const addFooter = (pdf: jsPDF, pageWidth: number, pageHeight: number, margin: number) => {
  const footerY = pageHeight - 20; // More space from bottom
  // Page number in gray
  pdf.setTextColor(...PDF_CONFIG.COLORS.GRAY_MEDIUM);
  pdf.text(`${pdf.getCurrentPageInfo().pageNumber}`, pageWidth - margin - 20, footerY);
};

// New helper: load remote image and return base64 + pixel dimensions (FULL QUALITY - for logos and covers)
const loadImageUrlAsBase64 = async (url: string): Promise<{ base64: string | null, width: number, height: number } | null> => {
  try {
    return await new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          if (!ctx) return resolve(null);
          ctx.drawImage(img, 0, 0);
          const base64 = canvas.toDataURL('image/png');
          resolve({ base64, width: img.naturalWidth, height: img.naturalHeight });
        } catch (err) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  } catch (error) {
    console.error('Error loading image as base64:', error);
    return null;
  }
};

// OPTIMIZED loader for activity media images only (reduces PDF size significantly)
const loadActivityImageOptimized = async (
  url: string,
  maxPixelSize: number = 1200
): Promise<{ base64: string | null, width: number, height: number } | null> => {
  try {
    return await new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');

          // OTTIMIZZAZIONE: Ridimensiona l'immagine mantenendo l'aspect ratio
          const MAX_WIDTH = maxPixelSize; // Max width in px (configurable)
          const MAX_HEIGHT = maxPixelSize; // Max height in px (configurable)

          let width = img.naturalWidth;
          let height = img.naturalHeight;

          // Calcola il fattore di scala
          if (width > MAX_WIDTH || height > MAX_HEIGHT) {
            const widthRatio = MAX_WIDTH / width;
            const heightRatio = MAX_HEIGHT / height;
            const scale = Math.min(widthRatio, heightRatio);

            width = Math.floor(width * scale);
            height = Math.floor(height * scale);
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) return resolve(null);

          // Disegna l'immagine ridimensionata
          ctx.drawImage(img, 0, 0, width, height);

          // OTTIMIZZAZIONE: Usa JPEG con qualità 0.85 invece di PNG
          const base64 = canvas.toDataURL('image/jpeg', 0.85);

          resolve({ base64, width, height });
        } catch (err) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  } catch (error) {
    console.error('Error loading activity image as base64:', error);
    return null;
  }
};

// Function to create icon as base64 using actual Lucide React components
const createIconBase64 = async (type: string): Promise<string> => {
  try {
    // Check cache first
    if (iconCache.has(type)) {
      return iconCache.get(type)!;
    }

    // Create a temporary container for the icon
    const iconContainer = document.createElement('div');
    iconContainer.style.position = 'absolute';
    iconContainer.style.left = '-9999px';
    iconContainer.style.top = '-9999px';
    iconContainer.style.width = '16px';
    iconContainer.style.height = '16px';
    iconContainer.style.display = 'flex';
    iconContainer.style.alignItems = 'center';
    iconContainer.style.justifyContent = 'center';
    iconContainer.style.backgroundColor = 'transparent';

    document.body.appendChild(iconContainer);

    // Get the actual Lucide React component
    const IconComponent = getIconComponent(type);

    // Render the actual Lucide React component
    const iconElement = document.createElement('div');
    iconContainer.appendChild(iconElement);

    // Use ReactDOM to render the actual Lucide component with createRoot (React 18)
    const { createRoot } = await import('react-dom/client');
    const root = createRoot(iconElement);
    root.render(React.createElement(IconComponent, { width: 16, height: 16, color: '#000000' }));

    // Wait for React to render the component before capturing
    await new Promise(resolve => setTimeout(resolve, 100));

    // Use html2canvas to convert to image
    const canvas = await html2canvas(iconContainer, {
      backgroundColor: null,
      width: 16,
      height: 16,
      scale: 2 // Higher resolution
    });

    const imgData = canvas.toDataURL('image/png');

    // Clean up
    document.body.removeChild(iconContainer);

    // Cache the result
    iconCache.set(type, imgData);

    return imgData;
  } catch (error) {
    console.error('Error creating icon:', error);
    return '';
  }
};

// Replaced implementation: try loadImageUrlAsBase64, then fetch SVG and convert to data URL and draw on canvas
const loadLogoAsBase64 = async (size: 'small' | 'large'): Promise<string | null> => {
  try {
    // 1) Try the generic loader first
    const imgData = await loadImageUrlAsBase64(size === 'large' ? PDF_CONFIG.LOGO.URL : PDF_CONFIG.LOGO_SMALL.URL);
    if (imgData && imgData.base64) return imgData.base64;

    // 2) Fallback: fetch the SVG/asset text and convert to a data URL (works better for inline/local SVG)
    const resp = await fetch(size === 'large' ? PDF_CONFIG.LOGO.URL : PDF_CONFIG.LOGO_SMALL.URL);
    if (!resp.ok) return null;
    const svgText = await resp.text();
    const svgDataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgText);

    // 3) Draw the SVG data URL onto a canvas with configured high-res size, then export PNG base64
    const canvas = document.createElement('canvas');
    canvas.width = size === 'large' ? PDF_CONFIG.LOGO.CANVAS_WIDTH : PDF_CONFIG.LOGO_SMALL.CANVAS_WIDTH;
    canvas.height = size === 'large' ? PDF_CONFIG.LOGO.CANVAS_HEIGHT : PDF_CONFIG.LOGO_SMALL.CANVAS_HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const img = new Image();
    img.crossOrigin = 'anonymous';

    return await new Promise((resolve) => {
      img.onload = () => {
        try {
          // Clear and draw scaled SVG onto canvas
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const base64 = canvas.toDataURL('image/png');
          resolve(base64);
        } catch (err) {
          console.error('Error drawing SVG to canvas', err);
          resolve(null);
        }
      };
      img.onerror = (e) => {
        console.warn('Failed to load SVG as image for logo fallback', e);
        resolve(null);
      };
      img.src = svgDataUrl;
    });
  } catch (error) {
    console.error('Error loading logo as base64:', error);
    return null;
  }
};

// Helper to load and draw itinerary cover image (returns updated usedHeight)
const loadCoverImage = async (
  pdf: jsPDF,
  itinerary: Tables<'itineraries'> | undefined,
  pageWidth: number,
  contentWidth: number,
  prevUsedHeight: number
): Promise<number> => {
  try {
    const coverUrl = itinerary && (itinerary.image as unknown as string);
    if (coverUrl) {
      const imgData = await loadActivityImageOptimized(coverUrl);
      if (imgData && imgData.base64) {
        const pxToMm = (px: number) => (px / 96) * 25.4; // assume 96dpi
        const maxPxWidth = 700;
        const maxPxHeight = 400;

        const maxCoverWidthMM = Math.min(contentWidth, pxToMm(maxPxWidth));
        const pageHeight = pdf.internal.pageSize.getHeight();
        const maxCoverHeightMM = Math.min(pageHeight - PDF_CONFIG.SPACING.MARGIN * 2, pxToMm(maxPxHeight));

        const imgWidthMM = pxToMm(imgData.width || 1);
        const imgHeightMM = pxToMm(imgData.height || 1);

        const scale = Math.min(maxCoverWidthMM / imgWidthMM, maxCoverHeightMM / imgHeightMM);
        const finalWidth = imgWidthMM * scale;
        const finalHeight = imgHeightMM * scale;

        const coverX = (pageWidth - finalWidth) / 2;
        const coverY = prevUsedHeight + 4; // small spacing after logo

        pdf.addImage(imgData.base64, 'PNG', coverX, coverY, finalWidth, finalHeight);

        return coverY + finalHeight;
      }
    }
  } catch (err) {
    console.warn('Could not load itinerary cover image for header:', err);
  }

  return prevUsedHeight;
};
